import _ from 'lodash';
import { action, computed, observable, runInAction } from 'mobx';
import { RulesService } from '../../rules/services';
import { TagModel } from '../../rules/models/TagModel';
import { TagsGroupModel } from '../../rules/models/TagsGroupModel';
import { BindingGroup } from '../../field_bindings/types';
import { TestProjectService } from '../services';
import {
    TestProject,
    InputBindingMetadataDto,
    InputBindingApplication,
    InputBindingGroup,
    TopicMappingTargetItemGroupModel
} from '../types';

// Owns available input metadata, selection validity and selection commands.
// Creation and existing-project editing each create an instance, so they share rules without sharing choices.
export default class TestProjectInputSelectionStore {
    @computed
    get isReadOnly() {
        return !this.canEdit();
    }

    @observable
    inputBindingsLoadError: string | undefined;

    @observable
    fieldBindingsLoadError: string | undefined;

    @observable
    ruleDataLoadError: string | undefined;

    @observable
    currentProjectTags: TagModel[] = [];

    @observable
    currentProjectTagGroups: TagsGroupModel[] = [];

    @observable
    isLoadingRuleData: boolean = false;

    @observable
    topicInputsType: TestProject['inputsType'] | undefined;

    @observable
    selectedTopicTags: string[] = [];

    @observable
    inputBindingsMetadata: InputBindingMetadataDto[] = [];

    @observable
    fieldBindingGroups: BindingGroup[] = [];

    @observable
    isLoadingFieldBindingGroups: boolean = false;

    @observable
    isLoadingInputBindingsMetadata: boolean = false;

    @observable
    selectedInputBindings: string[] = [];

    @observable
    selectedAppForBindings: string = '';

    @observable
    selectedInputGroupForBindings: string = '';

    @observable
    selectedProjectFields: string[] = [];

    @observable
    topicsSearchTerm: string = '';

    @observable
    activeTopicsTab: string = '1';

    @observable
    selectedTopicsAutocomplete: string = '';

    @observable
    selectedTopicsFilter: string = '';

    // Retries can overlap within this instance; only the latest response for each metadata source may apply.
    private bindingsLoadRequest = 0;
    private fieldsLoadRequest = 0;
    private rulesLoadRequest = 0;
    private savedInputs: Pick<TestProject, 'inputsType' | 'topics'> | undefined;

    constructor(
        readonly projectId: string,
        private service: TestProjectService,
        private rulesService: RulesService,
        inputType: TestProject['inputsType'] | undefined,
        private canEdit: () => boolean = () => true
    ) {
        this.topicInputsType = inputType;
    }

    @action.bound
    setActiveTopicsTab(tab: string) {
        this.activeTopicsTab = tab;
    }

    @action.bound
    setSelectedTopicsAutocomplete(value: string) {
        this.selectedTopicsAutocomplete = value;
    }

    @action.bound
    filterSelectedTopics(value: string) {
        this.selectedTopicsAutocomplete = value;
        this.selectedTopicsFilter = value;
    }

    @computed
    get metadataError() {
        switch (this.topicInputsType) {
            case 'Tags':
                return this.ruleDataLoadError;
            case 'Bindings':
                return this.inputBindingsLoadError;
            case 'ProjectFields':
                return this.fieldBindingsLoadError;
            default:
                return undefined;
        }
    }

    @computed
    get isLoadingMetadata() {
        switch (this.topicInputsType) {
            case 'Tags':
                return this.isLoadingRuleData;
            case 'Bindings':
                return this.isLoadingInputBindingsMetadata;
            case 'ProjectFields':
                return this.isLoadingFieldBindingGroups;
            default:
                return false;
        }
    }

    @computed
    get bindingApplications(): InputBindingApplication[] {
        return this.inputBindingsMetadata.map(app => {
            let groups: InputBindingGroup[];

            if (app.applicationType === 'ApplicationDefinitionConditional') {
                groups = app.inputGroups.map(group => ({
                    id: group.inputGroupId,
                    name: group.inputGroupName,
                    inputs: group.inputs
                }));
            } else {
                groups = [{ id: '', name: '', inputs: app.inputs }];
            }

            return {
                id: app.applicationId,
                name: app.applicationName,
                groups
            };
        });
    }

    @computed
    get currentApplicationInputGroups() {
        const application = this.bindingApplications.find(app => app.id === this.selectedAppForBindings);

        if (!application) {
            return [];
        }

        return application.groups.filter(group => group.id).map(group => ({ id: group.id, name: group.name }));
    }

    @computed
    get selectedBindingGroup() {
        const application = this.bindingApplications.find(app => app.id === this.selectedAppForBindings);

        return application?.groups.find(group => group.id === this.selectedInputGroupForBindings);
    }

    @computed
    get availableInputs(): { id: string; name: string; group: string }[] {
        switch (this.topicInputsType) {
            case 'Tags':
                return _.flatMap(this.currentProjectTags, tag => {
                    if (!tag.id) {
                        return [];
                    }

                    const group = this.currentProjectTagGroups.find(candidate => candidate.id === tag.groupId);

                    return [{ id: tag.id, name: tag.name, group: group?.name || '' }];
                });
            case 'Bindings': {
                const inputs = this.selectedBindingGroup?.inputs || [];

                return inputs.map(input => ({ id: input.inputId, name: input.name, group: input.section }));
            }
            case 'ProjectFields':
                return _.flatMap(this.fieldBindingGroups, group => {
                    const fields = _.uniqBy(group.fields, 'inputId');

                    return fields.map(field => {
                        const section = group.sections.find(candidate => candidate.sectionId === field.sectionId);

                        return {
                            id: field.inputId,
                            name: field.name,
                            group: `${group.name}/${section?.name || ''}`
                        };
                    });
                });
            default:
                return [];
        }
    }

    @computed
    get availableInputsById() {
        return new Map(this.availableInputs.map(input => [input.id, input]));
    }

    @computed
    get mappingOptions() {
        const groups = _.groupBy(this.availableInputs, 'group');

        return Object.entries(groups).map(([name, inputs]) => {
            const items = inputs.map(input => ({ id: input.id, name: input.name }));

            return new TopicMappingTargetItemGroupModel(name, items);
        });
    }

    @action.bound
    async loadMetadata() {
        await Promise.all([
            this.loadProjectRuleData(),
            this.loadProjectInputBindingsMetadata(),
            this.loadProjectFieldBindingsGroups()
        ]);
    }

    @action.bound
    initialize(project: Pick<TestProject, 'inputsType' | 'topics' | 'applicationId' | 'inputGroupId'>) {
        this.savedInputs = { inputsType: project.inputsType, topics: [...project.topics] };
        this.topicInputsType = project.inputsType;
        this.selectedAppForBindings = project.applicationId || '';
        this.selectedInputGroupForBindings = project.inputGroupId || '';
        this.selectedTopicTags = project.inputsType === 'Tags' ? project.topics.map(topic => topic.value) : [];
        this.selectedInputBindings = project.inputsType === 'Bindings' ? project.topics.map(topic => topic.value) : [];
        this.selectedProjectFields =
            project.inputsType === 'ProjectFields' ? project.topics.map(topic => topic.value) : [];
    }

    getTopicName(id: string) {
        return this.availableInputsById.get(id)?.name;
    }

    // Only inputs present when editing opened can have saved baselines.
    isSavedInput(id: string) {
        const savedInputs = this.savedInputs;

        if (!savedInputs) {
            return false;
        }

        return this.topicInputsType === savedInputs.inputsType && savedInputs.topics.some(topic => topic.value === id);
    }

    @computed
    get topics() {
        switch (this.topicInputsType) {
            case 'Tags':
                return this.selectedTopicTags;
            case 'Bindings':
                return this.selectedInputBindings;
            case 'ProjectFields':
                return this.selectedProjectFields;
            default:
                return [];
        }
    }

    @computed
    get hasInvalidTopics() {
        if (this.metadataError) {
            return true;
        }

        if (this.isLoadingMetadata) {
            return false;
        }

        return this.topics.some(id => !this.availableInputsById.has(id));
    }

    @computed
    get hasValidBindingSelection() {
        const inputIds = new Set(this.selectedBindingGroup?.inputs.map(input => input.inputId));
        return this.selectedInputBindings.every(binding => inputIds.has(binding));
    }

    canSelectBindingGroup(applicationId: string, inputGroupId: string) {
        if (this.isLoadingInputBindingsMetadata || this.inputBindingsLoadError) {
            return false;
        }

        if (!this.selectedAppForBindings || !this.hasValidBindingSelection) {
            return true;
        }

        return this.selectedAppForBindings === applicationId && this.selectedInputGroupForBindings === inputGroupId;
    }

    @action.bound
    removeTopic(id: string) {
        switch (this.topicInputsType) {
            case 'Tags':
                this.toggleTopicTag(id, false);
                break;
            case 'Bindings':
                this.toggleInputBinding(id, false, this.selectedAppForBindings);
                break;
            case 'ProjectFields':
                this.toggleTopicProjectField(id, false);
                break;
            default:
                return;
        }
    }

    @action.bound
    setTopicInputsType(type: TestProject['inputsType']) {
        if (this.isReadOnly) {
            return;
        }

        this.topicInputsType = type;
    }

    @action.bound
    toggleTopicTag(tag: string, checked: boolean) {
        if (this.isReadOnly) {
            return;
        }

        if (checked) {
            this.selectedTopicTags.push(tag);
        } else {
            this.selectedTopicTags = this.selectedTopicTags.filter(t => t !== tag);
        }
    }

    @action.bound
    toggleTopicProjectField(fieldId: string, checked: boolean) {
        if (this.isReadOnly) {
            return;
        }

        if (checked) {
            this.selectedProjectFields.push(fieldId);
        } else {
            this.selectedProjectFields = this.selectedProjectFields.filter(t => t !== fieldId);
        }
    }

    @action.bound
    toggleInputBinding(binding: string, checked: boolean, applicationId: string, inputGroupId?: string) {
        if (this.isReadOnly) {
            return;
        }

        if (checked) {
            if (!this.canSelectBindingGroup(applicationId, inputGroupId ?? '')) {
                return;
            }

            this.selectedInputBindings.push(binding);
            this.selectedAppForBindings = applicationId;
            this.selectedInputGroupForBindings = inputGroupId ?? '';

            // Repair against the chosen group; validity belongs to the draft, not the selector's mount.
            const selectedInputs = this.selectedBindingGroup?.inputs || [];
            this.selectedInputBindings = this.selectedInputBindings.filter(id =>
                selectedInputs.some(input => input.inputId === id)
            );
        } else {
            this.selectedInputBindings = this.selectedInputBindings.filter(b => b !== binding);
        }

        if (this.selectedInputBindings.length === 0) {
            this.selectedAppForBindings = '';
            this.selectedInputGroupForBindings = '';
        }
    }

    @action.bound
    setTopicsSearchTerm(term: string) {
        if (this.isReadOnly) {
            return;
        }

        this.topicsSearchTerm = term;
    }

    @action.bound
    async loadProjectRuleData() {
        const projectId = this.projectId;
        const request = ++this.rulesLoadRequest;

        this.ruleDataLoadError = undefined;
        this.isLoadingRuleData = true;

        try {
            const [tags, tagGroups] = await Promise.all([
                this.rulesService.getTagsByProject(projectId),
                this.rulesService.getTagsGroupsByProject(projectId)
            ]);

            if (request !== this.rulesLoadRequest) {
                return;
            }

            if (tags === null || tagGroups === null) {
                throw new Error('Failed to load project rule data');
            }

            runInAction(() => {
                this.currentProjectTags = tags;
                this.currentProjectTagGroups = tagGroups;
            });
        } catch (error) {
            if (request === this.rulesLoadRequest) {
                this.ruleDataLoadError = 'Failed to load project rule data';
            }
        } finally {
            if (request === this.rulesLoadRequest) {
                runInAction(() => {
                    this.isLoadingRuleData = false;
                });
            }
        }
    }

    @action.bound
    async loadProjectInputBindingsMetadata() {
        const projectId = this.projectId;
        const request = ++this.bindingsLoadRequest;

        this.inputBindingsLoadError = undefined;
        this.isLoadingInputBindingsMetadata = true;

        try {
            const result = await this.service.getInputBindings(projectId);

            if (request !== this.bindingsLoadRequest) {
                return;
            }

            if (result.isErr()) {
                throw result.error;
            }

            this.setInputBindingsMetadata(result.value);
        } catch (error) {
            if (request === this.bindingsLoadRequest) {
                this.inputBindingsLoadError = 'Failed to load project input bindings';
            }
        } finally {
            if (request === this.bindingsLoadRequest) {
                runInAction(() => {
                    this.isLoadingInputBindingsMetadata = false;
                });
            }
        }
    }

    @action.bound
    async loadProjectFieldBindingsGroups() {
        const projectId = this.projectId;
        const request = ++this.fieldsLoadRequest;

        this.fieldBindingsLoadError = undefined;
        this.isLoadingFieldBindingGroups = true;

        try {
            const result = await this.service.getProjectFieldGroups(projectId);

            if (request !== this.fieldsLoadRequest) {
                return;
            }

            if (result.isErr()) {
                throw result.error;
            }

            this.setProjectFieldBindingGroups(result.value);
        } catch (error) {
            if (request === this.fieldsLoadRequest) {
                this.fieldBindingsLoadError = 'Failed to load project field bindings';
            }
        } finally {
            if (request === this.fieldsLoadRequest) {
                runInAction(() => {
                    this.isLoadingFieldBindingGroups = false;
                });
            }
        }
    }

    @action.bound
    setInputBindingsMetadata(metadata: InputBindingMetadataDto[]) {
        this.inputBindingsMetadata = metadata;
    }

    @action.bound
    setProjectFieldBindingGroups(groups: BindingGroup[]) {
        this.fieldBindingGroups = groups;
    }
}
